import { ButtonUI, InputField, MessageErrors, SkeletonImage, SkeletonLine } from '@/components';
import { INFO_ORDER_USER, orderStatusText, ORDER_STATUS, REGEX_PHONE_NUMBER } from '@/constant';
import { useAppDispatch, useAppSelector, useAppSWR, useIsomorphicLayoutEffect } from '@/hooks';
import { DefaultLayout } from '@/layouts';
import { IParamGetAllOrder, IParamsGetOrdersWithId, IPreviewOrderInfo } from '@/models';
import { CartItemOrder } from '@/modules/cart/components';
import { orderActions, orderSelectors } from '@/store/reducers/orderSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import React, { ReactElement, useEffect, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';

const DynamicEmptyOrder = dynamic(() => import('@/components/EmptyOrder'), {
	ssr: false,
});

const CheckOrder = () => {
	const [isModeSubmit, setIsModeSubmit] = useState<boolean>(false);

	const dispatch = useAppDispatch();
	const { dataOrder } = useAppSelector(orderSelectors);

	const formsSchema = yup.object<Record<keyof IParamGetAllOrder, yup.AnySchema>>().shape({
		orderId: yup.string().required('Vui lòng nhập mã đơn hàng'),
		phone: yup
			.string()
			.required('Vui lòng nhập số điện thoại')
			.matches(REGEX_PHONE_NUMBER, 'Lỗi định dạng SĐT'),
	});

	const initalValues: IParamGetAllOrder = {
		phone: '',
		orderId: '',
	};
	const methods: UseFormReturn<IParamGetAllOrder> = useForm<IParamGetAllOrder>({
		mode: 'onChange',
		shouldFocusError: true,
		defaultValues: initalValues,
		resolver: yupResolver(formsSchema),
	});

	useEffect(() => {
		const valuesHasInfo = localStorage.getItem(INFO_ORDER_USER);
		if (valuesHasInfo) {
			methods.reset(JSON.parse(valuesHasInfo));
		}
	}, []);

	const { data: dataPreviewNonSubmitOrder, isValidating: isValidatingNonSubmit } =
		useAppSWR<IPreviewOrderInfo>(
			{
				url: `/v1/order_all_user`,
				method: 'POST',
				baseURL: process.env.NEXT_PUBLIC_DOMAIN_API_AUTH!,
			},
			{ isPaused: () => isModeSubmit },
		);

	// const { lastElementRef } = useLastElement(
	// 	isValidating,
	// 	response?.pagination?.totalPage! >= size + 1,
	// 	() => {
	// 		setSize(size + 1);
	// 	},
	// );

	useIsomorphicLayoutEffect(() => {
		window.scrollTo({ behavior: 'smooth', top: 0 });
	}, [dataOrder?.isRequestGetOrderWithId]);

	const onSubmit = (values: IParamsGetOrdersWithId) => {
		dispatch(
			orderActions.getOrderWithIdRequest({
				params: { orderId: values?.orderId, phone: values?.phone },
				onSuccess: () => {
					setIsModeSubmit(true);
				},
			}),
		);
	};

	return (
		<div className='lg:container'>
			<form onSubmit={methods.handleSubmit(onSubmit)} className='relative'>
				<div className='p-2.5 lg:p-0 '>
					<div className='flex flex-col-reverse lg:flex-row relative gap-[40px] lg:gap-[70px] py-4 lg:py-16'>
						<div className='flex-auto lg:sticky lg:top-4 h-fit'>
							<div className='flex flex-col gap-12'>
								<div className='flex flex-col gap-3 lg:gap-6'>
									<h3 className='text-large font-bold text-primary mb-4 lg:mb-11'>
										Nhập thông tin đơn hàng
									</h3>

									<Controller
										render={({ field, formState }) => (
											<InputField
												typeStyleForms={{ animation: true, labelInsideBorder: true }}
												id='phone'
												{...field}
												type='tel'
												label='Số điện thoại'
												validationMessage={<MessageErrors name='phone' errors={formState.errors} />}
											/>
										)}
										name='phone'
										control={methods.control}
									/>
									<Controller
										render={({ field, formState }) => (
											<InputField
												typeStyleForms={{ animation: true, labelInsideBorder: true }}
												id='orderId'
												{...field}
												type='tel'
												label='Mã đơn hàng'
												validationMessage={
													<MessageErrors name='orderId' errors={formState.errors} />
												}
											/>
										)}
										name='orderId'
										control={methods.control}
									/>
								</div>
							</div>
							<div className='hidden lg:block'>
								<ButtonUI
									color='primary'
									htmlType='submit'
									text='Kiểm tra'
									className='text-medium flex mt-12 justify-center w-full'
								/>
							</div>
						</div>
						<div className='h-full lg:sticky lg:top-0 flex-auto lg:flex-[0_0_55%] lg:max-w-[55%]'>
							<div className='bg-base-100 h-fit p-2.5 lg:p-[54px]'>
								{!isModeSubmit ? (
									isValidatingNonSubmit ? (
										<div className='flex gap-2 items-center'>
											<div className='w-[90px] aspect-square'>
												<SkeletonImage />
											</div>
											<SkeletonLine />
										</div>
									) : dataPreviewNonSubmitOrder?.orders?.length ? (
										dataPreviewNonSubmitOrder?.orders?.map((order, index) => (
											<div key={index}>
												<div className='bg-[#F4F4F5] text-primary flex justify-between items-center  py-[6px] px-[9px]'>
													<div className='flex flex-col justify-start lg:flex-row lg:items-center gap-2'>
														<div className='text-small lg:text-medium font-bold'>
															Đơn hàng #{order?.id}
														</div>
														<div
															className={classNames([
																order.status === ORDER_STATUS.NEW &&
																	'text-primary bg-[rgba(10_,149,_158,_0.1)]',
																order.status === ORDER_STATUS.SUCCESS &&
																	'text-[#027A48] bg-[rgba(10,_158,_87,_0.1)]',
																order.status === ORDER_STATUS.FAILED &&
																	'text-red-custom bg-red-custom/25',
																'font-bold py-[4px] px-[6px] text-small lg:text-base lg:py-[6px] lg:px-[12px] w-fit uppercase rounded-[5px] lg:rounded-full',
															])}
														>
															{orderStatusText[order.status]}
														</div>
													</div>
													<div className='text-medium lg:text-large font-bold'>
														{Number(order?.cart_info?.payment?.total)?.toLocaleString('it-IT')}đ
													</div>
												</div>
												{order?.cart_info?.cart_items?.map((item, i) => (
													<CartItemOrder data={item} key={i} />
												))}
											</div>
										))
									) : (
										<DynamicEmptyOrder />
									)
								) : (
									<>
										{dataOrder?.isRequestGetOrderWithId ? (
											<div className='flex gap-2 items-center'>
												<div className='w-[90px] aspect-square'>
													<SkeletonImage />
												</div>
												<SkeletonLine />
											</div>
										) : dataOrder?.dataOrderWithId?.id ? (
											<React.Fragment>
												<div className='bg-[#F4F4F5] text-primary flex justify-between items-center  py-[6px] px-[9px]'>
													<div className='flex flex-col justify-start lg:flex-row lg:items-center gap-2'>
														<div className='text-small lg:text-medium font-bold'>
															Đơn hàng #{dataOrder?.dataOrderWithId?.id}
														</div>
														<div
															className={classNames([
																dataOrder?.dataOrderWithId.status === ORDER_STATUS.NEW &&
																	'text-primary bg-[rgba(10_,149,_158,_0.1)]',
																dataOrder?.dataOrderWithId.status === ORDER_STATUS.SUCCESS &&
																	'text-[#027A48] bg-[rgba(10,_158,_87,_0.1)]',
																dataOrder?.dataOrderWithId.status === ORDER_STATUS.FAILED &&
																	'text-red-custom bg-red-custom/25',
																'font-bold py-[4px] px-[6px] text-small lg:text-base lg:py-[6px] lg:px-[12px] w-fit uppercase rounded-[5px] lg:rounded-full',
															])}
														>
															{orderStatusText[dataOrder?.dataOrderWithId.status]}
														</div>
													</div>
													<div className='text-medium lg:text-large font-bold'>
														{Number(
															dataOrder?.dataOrderWithId?.cart_info?.payment?.total,
														)?.toLocaleString('it-IT')}
														đ
													</div>
												</div>
												{dataOrder?.dataOrderWithId?.cart_info?.cart_items?.map((item, i) => (
													<CartItemOrder data={item} key={i} />
												))}
											</React.Fragment>
										) : (
											<DynamicEmptyOrder />
										)}
									</>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className='flex shadow-top mb-4 flex-row-reverse gap-4 bg-base-100 items-center lg:hidden sticky bottom-0'>
					<ButtonUI
						color='primary'
						htmlType='submit'
						text='Kiểm tra'
						className='text-small rounded-none !my-0 flex justify-center w-full'
					/>
				</div>
			</form>
		</div>
	);
};

export default CheckOrder;

CheckOrder.getLayout = (page: ReactElement) => (
	<DefaultLayout
		seoProps={{
			title: 'Kiểm tra đơn hàng | Nga Liem',
			openGraph: { title: 'Kiểm tra đơn hàng | Nga Liem' },
		}}
		forceTitleRender
	>
		{page}
	</DefaultLayout>
);
